// @flow

import * as React from "react"
import {isEqual, debounce, isNil, isEmpty} from "lodash"
import {withTranslation} from "react-i18next"
import ClassNames from "classnames"

import PriceInput from "./price-input"
import NumericInput from "./numeric-input"
import NumericStepInput from "./numeric-step-input"
import Icon from "./icon"
import type {BabyListItem, ProductOrSet, ProductSet} from "../types"

type Props = {
  babyListItem: ?BabyListItem,
  product: ProductOrSet,
  productSet: ?ProductSet,
  onUpdate: (BabyListItem) => void,
  t: any,
}

type State = {
  edits: BabyListItem,
  isDirty: boolean,
  key: string,
}

class ProductListItemSettings extends React.Component<Props, State> {
  handleUpdate: (SyntheticInputEvent<HTMLInputElement>) => void
  handleUpdatePrice: (string, string) => void
  handleAmountUpdate: (number) => void
  persistUpdate: (BabyListItem) => void

  constructor(props: Props) {
    super(props)

    this.handleUpdate = this.handleUpdate.bind(this)
    this.handleUpdatePrice = this.handleUpdatePrice.bind(this)
    this.handleAmountUpdate = this.handleAmountUpdate.bind(this)
    this.persistUpdate = debounce(this.persistUpdate.bind(this), 200, {
      leading: false,
      trailing: true,
    })

    const {product} = props

    this.state = {
      key: product.id + (product.isSet ? "-set" : "-no-set"),
      isDirty: false,
      edits: {...props.babyListItem},
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (isNil(prevProps.babyListItem) && !isNil(this.props.babyListItem)) {
      this.setState({
        edits: {...this.props.babyListItem},
      })
    }
  }

  handleUpdate(e: SyntheticInputEvent<HTMLInputElement>) {
    e.persist && e.persist()
    const target = e.target
    const value = target.type === "checkbox" ? target.checked : target.value
    const name = target.name
    const {edits} = this.state
    const update = {
      ...edits,
      [name]: value,
    }
    const isDirty = !isEqual(this.props.babyListItem, edits)
    this.setState({
      edits: update,
      isDirty,
    })
    this.persistUpdate(update)
  }

  handleUpdatePrice(property: string, value: string) {
    const {babyListItem} = this.props
    const {edits} = this.state
    const update = {
      ...edits,
      [property]: value,
    }
    const isDirty = !isEqual(babyListItem, edits)
    this.setState({
      edits: update,
      isDirty,
    })
    this.persistUpdate(update)
  }

  handleAmountUpdate(amount) {
    const {t, babyListItem, product, productSet} = this.props
    const {edits} = this.state
    const update = {
      ...edits,
      amount,
    }
    if (
      (productSet &&
        productSet.isDivisible &&
        babyListItem &&
        babyListItem.parentId &&
        amount < product.typicalAmount) ||
      (!productSet &&
        babyListItem &&
        babyListItem.parentId &&
        amount < product.typicalAmount)
    ) {
      alert(
        t("intake.babyList.item.settings.amountMinimum", {
          typicalAmount: product.typicalAmount,
        })
      )
      return
    }
    const isDirty = !isEqual(babyListItem, edits)
    this.setState({
      edits: update,
      isDirty,
    })
    this.persistUpdate(update)
  }

  persistUpdate(babyListItem: BabyListItem) {
    this.props.onUpdate(babyListItem)
  }

  renderBabyListItemSettings() {
    const {t, product} = this.props
    const {edits, key} = this.state
    return (
      <>
        {product.description && product.description !== "" ? (
          <div className="row desc">
            <div dangerouslySetInnerHTML={{__html: product.description}}></div>
          </div>
        ) : null}
        <div className="row" key={"baby-list-item-settings-" + key}>
          <div className="l">
            {!product.isSet ? (
              <>
                <div className="cell isPimp">
                  <input
                    type="checkbox"
                    id={`isPimp[${edits.id}]`}
                    name="isPimp"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isPimp}
                  />
                  <label
                    htmlFor={`isPimp[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isPimp ? "is-active" : "")
                    }
                  >
                    <Icon name="icon-brush" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      className="form-control form-control-sm"
                      name="pricePimp"
                      value={edits.pricePimp}
                      readOnly={!edits.isPimp}
                      onChange={this.handleUpdatePrice}
                    />
                  </div>
                </div>
                <div className="cell priceExtra">
                  <input
                    type="checkbox"
                    id={`isExtra[${edits.id}]`}
                    name="isExtra"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isExtra}
                  />
                  <label
                    htmlFor={`isExtra[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isExtra ? "is-active" : "")
                    }
                  >
                    <Icon name="icon-calculator" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      className="form-control form-control-sm"
                      name="priceExtra"
                      id={`priceExtra[${edits.id}]`}
                      readOnly={!edits.isExtra}
                      value={edits.priceExtra}
                      onChange={this.handleUpdatePrice}
                    />
                  </div>
                </div>
                <div className="cell priceSelling">
                  <label
                    htmlFor={`priceSelling[${edits.id}]`}
                    className="cell-icon-label "
                  >
                    <Icon name="icon-euro" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      className="form-control form-control-sm"
                      name="priceSelling"
                      id={`priceSelling[${edits.id}]`}
                      value={edits.priceSelling}
                      onChange={this.handleUpdatePrice}
                    />
                  </div>
                </div>
                <div className="cell isStar">
                  <input
                    type="checkbox"
                    id={`isStar[${edits.id}]`}
                    name="isStar"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isStar}
                  />
                  <label
                    htmlFor={`isStar[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isStar ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={"icon-" + (edits.isStar ? "star" : "star-empty")}
                    />
                  </label>
                </div>
                <div className="cell isRare">
                  <input
                    type="checkbox"
                    id={`isRare[${edits.id}]`}
                    name="isRare"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isRare}
                  />
                  <label
                    htmlFor={`isRare[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isRare ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={
                        "icon-" + (edits.isRare ? "warning-alt" : "warning-alt")
                      }
                    />
                  </label>
                </div>
                <div className="cell inEstimate">
                  <input
                    type="checkbox"
                    id={`inEstimate[${edits.id}]`}
                    name="inEstimate"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.inEstimate}
                  />
                  <label
                    htmlFor={`inEstimate[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.inEstimate ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={
                        "icon-" +
                        (edits.inEstimate
                          ? "checkbox-checked"
                          : "checkbox-unchecked")
                      }
                    />
                  </label>
                </div>
                <div className="cell isFirstPickup">
                  <input
                    type="checkbox"
                    id={`isFirstPickup[${edits.id}]`}
                    name="isFirstPickup"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isFirstPickup}
                  />
                  <label
                    htmlFor={`isFirstPickup[${edits.id}]`}
                    className={
                      "cell-icon-label " +
                      (!edits.isFirstPickup ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={
                        "icon-" +
                        (!edits.isFirstPickup
                          ? "use-hand-truck"
                          : "use-hand-truck")
                      }
                    />
                  </label>
                </div>
                {edits.canTogglePriceTbd && (
                  <div className="cell isPriceTbd">
                    <input
                      type="checkbox"
                      id={`isPriceTbd[${edits.id}]`}
                      name="isPriceTbd"
                      onChange={this.handleUpdate}
                      required={true}
                      checked={edits.isPriceTbd}
                    />
                    <label
                      htmlFor={`isPriceTbd[${edits.id}]`}
                      className={
                        "cell-icon-label " +
                        (edits.isPriceTbd ? "is-active" : "")
                      }
                    >
                      <Icon name="icon-tbd" />
                    </label>
                  </div>
                )}
                <div className="cell leadFee">
                  <label
                    htmlFor={`leadFee[${edits.id}]`}
                    className="cell-icon-label "
                  >
                    %
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      className="form-control form-control-sm"
                      name="leadFee"
                      id={`leadFee[${edits.id}]`}
                      value={edits.leadFee}
                      onChange={this.handleUpdatePrice}
                    />
                  </div>
                </div>
                {edits.webshopSource == "intake_not_invoiced" ? (
                  <div className="cell webshopSource">
                    <div className="cell-input" style={{maxWidth: "none"}}>
                      <select
                        name="webshopSource"
                        value={edits.webshopSource}
                        onChange={this.handleUpdate}
                      >
                        <option value=""></option>
                        <option value="intake_not_invoiced">
                          intake_not_invoiced
                        </option>
                      </select>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="cell priceExtra">
                  <input
                    type="checkbox"
                    id={`isExtra[${edits.id}]`}
                    name="isExtra"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isExtra}
                  />
                  <label
                    htmlFor={`isExtra[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isExtra ? "is-active" : "")
                    }
                  >
                    <Icon name="icon-calculator" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      className="form-control form-control-sm"
                      name="priceExtra"
                      id={`priceExtra[${edits.id}]`}
                      readOnly={!edits.isExtra}
                      value={edits.priceExtra}
                      onChange={this.handleUpdatePrice}
                    />
                  </div>
                </div>
                <div className="cell isStar">
                  <input
                    type="checkbox"
                    id={`isStar[${edits.id}]`}
                    name="isStar"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.isStar}
                  />
                  <label
                    htmlFor={`isStar[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.isStar ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={"icon-" + (edits.isStar ? "star" : "star-empty")}
                    />
                  </label>
                </div>
                <div className="cell inEstimate">
                  <input
                    type="checkbox"
                    id={`inEstimate[${edits.id}]`}
                    name="inEstimate"
                    onChange={this.handleUpdate}
                    required={true}
                    checked={edits.inEstimate}
                  />
                  <label
                    htmlFor={`inEstimate[${edits.id}]`}
                    className={
                      "cell-icon-label " + (edits.inEstimate ? "is-active" : "")
                    }
                  >
                    <Icon
                      name={
                        "icon-" +
                        (edits.inEstimate
                          ? "checkbox-checked"
                          : "checkbox-unchecked")
                      }
                    />
                  </label>
                </div>
              </>
            )}

            <div className="cell amount">
              <div className="cell-input">
                <NumericStepInput
                  className=""
                  name="amount"
                  min={0}
                  id={`amount[${edits.id}]`}
                  value={parseInt(edits.amount)}
                  onChange={this.handleAmountUpdate}
                />

                {/*<small className="ml-1">(&gt;={product.typicalAmount})</small>*/}
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="cell remarks">
            <textarea
              className="form-control form-control-sm"
              name="remarks"
              placeholder={t("intake.babyList.item.settings.remarks")}
              value={edits.remarks}
              onChange={this.handleUpdate}
            />
          </div>
        </div>
      </>
    )
  }

  renderProductListItemSettings() {
    const {product, t} = this.props
    const {key} = this.state
    return (
      <>
        {product.description && product.description !== "" && (
          <div className="row desc">
            <div dangerouslySetInnerHTML={{__html: product.description}}></div>
          </div>
        )}
        <div className="row" key={"product-settings-" + key}>
          <div className="l">
            {!product.isSet ? (
              <>
                <div className="cell isPimp">
                  <label className="cell-label">
                    <Icon name={"icon-brush"} />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      name="pricePimp"
                      className="form-control form-control-sm"
                      value={product.pricePimp}
                      readOnly={true}
                    />
                  </div>
                </div>
                <div className="cell priceExtra">
                  <label className="cell-label">
                    <Icon name="icon-calculator" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      name="priceExtra"
                      className="form-control form-control-sm"
                      value={product.priceExtra}
                      id={`priceExtra[${product.id}]`}
                      readOnly={true}
                    />
                  </div>
                </div>
                <div className="cell priceSelling">
                  <label className="cell-label">
                    <Icon name="icon-euro" />
                  </label>
                  <div className="cell-input">
                    <PriceInput
                      name="priceSelling"
                      className="form-control form-control-sm"
                      id={`priceSelling[${product.id}]`}
                      value={product.priceSelling}
                      readOnly={true}
                    />
                  </div>
                </div>
                <div className="cell isStar">
                  <label htmlFor={`isStar[${product.id}]`}>
                    <Icon name="icon-star-empty" />
                  </label>
                </div>

                <div className="cell isRare">
                  <label
                    htmlFor={`isRare[${product.id}]`}
                    className={`cell-icon-label ${
                      product.isRare && "is-active"
                    }`}
                  >
                    <Icon name="icon-warning-alt" />
                  </label>
                </div>
                {product.isPriceTbd && (
                  <div className="cell isPriceTbd">
                    <label htmlFor={`isPriceTbd[${product.id}]`}>
                      <Icon name="icon-tbd" />
                    </label>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="cell isStar">
                  <label htmlFor={`isStar[${product.id}]`}>
                    <Icon name="icon-star-empty" />
                  </label>
                </div>
              </>
            )}

            <div className="cell amount">
              <div className="cell-input">
                <NumericInput
                  className="form-control form-control-sm"
                  id={`amount[${product.id}]`}
                  defaultValue={parseInt(product.typicalAmount || 0)}
                  readOnly={true}
                />
                <label>{t("intake.products.amountSuffix")}</label>
              </div>
            </div>
          </div>
          <div className="r" />
        </div>
      </>
    )
  }

  render() {
    const {babyListItem} = this.props
    const {edits} = this.state
    const className = ClassNames("product-list-item-settings", {
      "is-editable": !isNil(babyListItem),
    })
    return (
      <div className={className}>
        {!isNil(babyListItem) && !isEmpty(edits)
          ? this.renderBabyListItemSettings()
          : this.renderProductListItemSettings()}
      </div>
    )
  }
}

export default withTranslation()(ProductListItemSettings)
